import React, { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const iconButtonVariants = cva(
  "inline-flex items-center justify-center h-8 w-8 rounded-md border border-border bg-background text-muted-foreground transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "hover:bg-accent/5 hover:text-foreground hover:border-border",
        view: "hover:border-accent hover:text-accent hover:bg-accent/5",
        olt: "hover:border-accent hover:text-accent hover:bg-accent/5",
        refresh: "hover:border-warning hover:text-warning hover:bg-warning/5",
        del: "hover:border-destructive hover:text-destructive hover:bg-destructive/5",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof iconButtonVariants> {
  icon: React.ReactNode;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, icon, ...props }, ref) => {
    if (process.env.NODE_ENV !== 'production' && !props["aria-label"]) {
      // biome-ignore lint/suspicious/noConsole: development-time accessibility warning
      console.warn("IconButton: aria-label is required for accessibility");
    }
    return (
      <button className={cn(iconButtonVariants({ variant, className }))} ref={ref} {...props}>
        {icon}
      </button>
    );
  },
);
IconButton.displayName = "IconButton";

export { IconButton, iconButtonVariants };
